import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsStringLiteral, parseAsInteger, parseAsBoolean } from 'nuqs';
import { Link } from 'wouter';
import { Plus, Send, MessageSquare, Users, BarChart3, Phone, Settings, Trash2, Edit, Calendar, Clock, AlertTriangle, ShieldX, FolderPlus, Folder, MoveHorizontal, ChevronLeft, ChevronUp, ChevronDown, Tag, Save, Eye, CheckCircle2, XCircle, PhoneOutgoing, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PermissionButton } from '@/components/ui/permission-button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { toE164Format } from '@/lib/phone';
import { MarketingPageHeader } from '@/components/marketing/MarketingPageHeader';
import { SectionHeader } from '@/components/marketing/SectionHeader';
import { PaginationControls } from '@/components/marketing/PaginationControls';
import { ContactListFilters } from '@/components/marketing/ContactListFilters';
import { ContactListTable } from '@/components/marketing/ContactListTable';
import { BulkActionsBar } from '@/components/marketing/BulkActionsBar';
import { DNCTable } from '@/components/marketing/DNCTable';
import { SaveListDialog } from '@/components/marketing/SaveListDialog';
import { EditListDialog } from '@/components/marketing/EditListDialog';
import { FolderNavigation } from '@/components/marketing/FolderNavigation';
import type { 
  SmsTemplate, 
  SmsTemplateFolder,
} from '@shared/schema';

// Local type definitions
interface SmsListType {
  id: number;
  name: string;
  description?: string;
  contactIds: string[];
  contactCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DNCEntry {
  id: string | number;
  phone?: string;
  email?: string;
  reason: string;
  type?: 'email' | 'sms' | 'both';
  addedAt?: string;
}

interface SmsAnalytic {
  id: number;
  smsId?: number;
  event: string;
  recipientPhone: string;
  recipientName?: string;
  recipientEmail?: string;
  recipientType?: string;
  messagePreview?: string;
  timestamp?: string;
}

interface SmsAnalyticsResponse {
  overview: {
    totalSent: number;
    delivered: number;
    failed: number;
    pending: number;
    deliveryRate: string;
  };
  timeline: Array<{
    date: string;
    sent: number;
    delivered: number;
    failed: number;
  }>;
  recentActivity: SmsAnalytic[];
  failureReasons: Array<{
    errorCode: string;
    count: number;
    message: string;
  }>;
}

interface SmsThrottling {
  dailyCount: number;
  monthlyCount: number;
}

// Agency phone number type for SMS-enabled numbers
interface AgencyPhoneNumber {
  id: number;
  phoneNumber: string;
  friendlyName: string | null;
  isDefault: boolean;
  smsEnabled: boolean;
}

export default function SmsMarketing() {
  // NUQS: URL-based state management for shareable URLs
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(["templates", "contacts", "analytics", "dnc"]).withDefault("templates")
  ); // Active tab (templates/contacts/analytics/dnc)
  const [folderId, setFolderId] = useQueryState("folder", parseAsInteger); // Selected folder ID for navigation
  const [typeFilter, setTypeFilter] = useQueryState(
    "type",
    parseAsStringLiteral(["all", "client", "prospect"]).withDefault("all")
  ); // Contact type filter (all/client/prospect)
  const [searchFilter, setSearchFilter] = useQueryState("search", { defaultValue: "" }); // Search query for contacts
  const [currentPage, setCurrentPage] = useQueryState("page", parseAsInteger.withDefault(1)); // Pagination page for contacts

  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [oneToOnePhone, setOneToOnePhone] = useState('');
  const [oneToOneMessage, setOneToOneMessage] = useState('');
  const [smsError, setSmsError] = useState<string | null>(null);
  const [bulkSendResult, setBulkSendResult] = useState<any>(null);
  const [dncPhone, setDncPhone] = useState('');
  const [dncReason, setDncReason] = useState('');
  const [dncSearch, setDncSearch] = useState('');

  // Folder management state
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<SmsTemplateFolder | null>(null);
  const [folderForm, setFolderForm] = useState({ name: "", description: "" });
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [selectedTemplateForMove, setSelectedTemplateForMove] = useState<SmsTemplate | null>(null);
  const [selectedMoveFolder, setSelectedMoveFolder] = useState<SmsTemplateFolder | null>(null);
  const [isQuickSmsDialogOpen, setIsQuickSmsDialogOpen] = useState(false);
  const [showQuickSmsClientSearch, setShowQuickSmsClientSearch] = useState(false);
  const [selectedQuickSmsClient, setSelectedQuickSmsClient] = useState<any>(null);
  const [selectedQuickSmsClientType, setSelectedQuickSmsClientType] = useState<'client' | 'prospect' | null>(null);
  const [showQuickSmsTemplateSelect, setShowQuickSmsTemplateSelect] = useState(false);
  const [selectedFromNumberId, setSelectedFromNumberId] = useState<string | null>(null);

  // Template editing state
  const [isCreateTemplateDialogOpen, setIsCreateTemplateDialogOpen] = useState(false);
  const [isEditTemplateDialogOpen, setIsEditTemplateDialogOpen] = useState(false);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState<SmsTemplate | null>(null);
  const [editTemplateForm, setEditTemplateForm] = useState({ name: "", content: "" });

  // Merge tags state
  const [showMergeTags, setShowMergeTags] = useState(false);
  const [mergeTagsTarget, setMergeTagsTarget] = useState<'create' | 'edit' | 'quick_sms'>('create');

  // Template sorting state
  const [templateSortField, setTemplateSortField] = useState<'name' | 'content' | 'createdAt' | null>(null);
  const [templateSortDirection, setTemplateSortDirection] = useState<'asc' | 'desc'>('asc');

  // Lists state
  const [sortField, setSortField] = useState<'name' | 'phone' | 'type' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isSaveListDialogOpen, setIsSaveListDialogOpen] = useState(false);
  const [isEditListDialogOpen, setIsEditListDialogOpen] = useState(false);
  const [listToEdit, setListToEdit] = useState<SmsListType | null>(null);
  const [editListName, setEditListName] = useState('');
  const [editListDescription, setEditListDescription] = useState('');
  const [editListSelectedContacts, setEditListSelectedContacts] = useState<Set<string>>(new Set());
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [listSearchFilter, setListSearchFilter] = useState('');
  const [contactsPerPage] = useState(10);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());

  // Deletion confirmation state
  const [templateToDelete, setTemplateToDelete] = useState<SmsTemplate | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<SmsTemplateFolder | null>(null);
  const [listToDelete, setListToDelete] = useState<SmsListType | null>(null);
  const [listDeleteDialogOpen, setListDeleteDialogOpen] = useState(false);
  const [dncEntryToDelete, setDncEntryToDelete] = useState<DNCEntry | null>(null);

  // Message preview modal state
  const [messagePreviewOpen, setMessagePreviewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<SmsAnalytic | null>(null);

  // Analytics filters state (local - not applied until button click)
  const [analyticsStatusFilterLocal, setAnalyticsStatusFilterLocal] = useState<string>('all');
  const [analyticsSearchFilterLocal, setAnalyticsSearchFilterLocal] = useState<string>('');
  const [analyticsDateRangeLocal, setAnalyticsDateRangeLocal] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  // Applied filters (trigger API calls)
  const [analyticsStatusFilter, setAnalyticsStatusFilter] = useState<string>('all');
  const [analyticsSearchFilter, setAnalyticsSearchFilter] = useState<string>('');
  const [analyticsDateRange, setAnalyticsDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  const applyAnalyticsFilters = () => {
    setAnalyticsStatusFilter(analyticsStatusFilterLocal);
    setAnalyticsSearchFilter(analyticsSearchFilterLocal);
    setAnalyticsDateRange(analyticsDateRangeLocal);
  };

  const clearAnalyticsFilters = () => {
    const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const defaultEnd = new Date().toISOString().split('T')[0];
    
    setAnalyticsStatusFilterLocal('all');
    setAnalyticsSearchFilterLocal('');
    setAnalyticsDateRangeLocal({ start: defaultStart, end: defaultEnd });
    
    setAnalyticsStatusFilter('all');
    setAnalyticsSearchFilter('');
    setAnalyticsDateRange({ start: defaultStart, end: defaultEnd });
  };

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Queries - moved before they're used
  const { data: templates = [] } = useQuery<SmsTemplate[]>({
    queryKey: ['/api/sms/templates'],
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { data: folders = [] } = useQuery<SmsTemplateFolder[]>({
    queryKey: ['/api/sms/folders'],
  });

  // Derive currentFolderView from folderId URL param
  // Ensure folderId is compared as number to match API response type
  const currentFolderView = folderId ? folders.find(f => f.id === Number(folderId)) || null : null;

  // Helper functions for template organization
  const getValidFolderIds = (): Set<number> => {
    return new Set(folders.map(f => Number(f.id)));
  };

  const getUnorganizedTemplates = (): SmsTemplate[] => {
    return templates.filter(t => !t.folderId);
  };

  const getTemplatesInFolder = (folderId: number): SmsTemplate[] => {
    return templates.filter(t => Number(t.folderId) === Number(folderId));
  };

  const validateFolderId = (id: number | null | undefined): number | null => {
    if (!id) return null;
    const validFolderIds = getValidFolderIds();
    return validFolderIds.has(Number(id)) ? Number(id) : null;
  };

  const { data: savedLists = [] } = useQuery<SmsListType[]>({
    queryKey: ['/api/sms/lists'],
  });

  const { data: contacts = [] } = useQuery<any[]>({
    queryKey: ['/api/sms/contacts'],
  });

  const { data: dncList = [] } = useQuery<DNCEntry[]>({
    queryKey: ['/api/sms/dnc'],
  });

  const { data: analytics } = useQuery<SmsAnalyticsResponse>({
    queryKey: ['/api/sms/analytics/campaigns', {
      startDate: analyticsDateRange.start,
      endDate: analyticsDateRange.end,
      status: analyticsStatusFilter,
      search: analyticsSearchFilter,
    }],
  });

  const { data: throttling } = useQuery<SmsThrottling>({
    queryKey: ['/api/sms/throttling/current_user'],
  });

  const { data: clients = [] } = useQuery({ queryKey: ["/api/clients"] });
  const { data: prospects = [] } = useQuery({ queryKey: ["/api/prospects"] });

  // Fetch SMS-enabled phone numbers for "From Number" selection
  const { data: smsPhoneNumbers = [] } = useQuery<AgencyPhoneNumber[]>({
    queryKey: ['/api/agency/phone-numbers/sms-enabled'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/agency/phone-numbers/sms-enabled');
      return res.json();
    },
  });

  // Check if SMS sending is available (agency has phone numbers OR system default exists)
  const { data: twilioSettings } = useQuery<{
    provisioningStatus: string;
    twilioConfigured: boolean;
    hasAgencyPhoneNumbers: boolean;
    hasSystemDefault: boolean;
    smsReady: boolean;
  }>({
    queryKey: ['/api/agency/twilio-settings'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/agency/twilio-settings');
      return res.json();
    },
  });
  const isSmsReady = twilioSettings?.smsReady ?? true; // Default to true to avoid flash of warning

  // Common merge tags for SMS templates
  const mergeTags = [
    { label: 'First Name', value: '{{first_name}}' },
    { label: 'Last Name', value: '{{last_name}}' },
    { label: 'Full Name', value: '{{full_name}}' },
    { label: 'Company', value: '{{company}}' },
    { label: 'Email', value: '{{email}}' },
    { label: 'Phone', value: '{{phone}}' },
    { label: 'Date', value: '{{date}}' },
    { label: 'Time', value: '{{time}}' },
  ];

  const insertMergeTag = (tag: string) => {
    if (mergeTagsTarget === 'create') {
      setNewTemplateContent(prev => prev + tag);
    } else if (mergeTagsTarget === 'edit') {
      setEditTemplateForm(prev => ({ ...prev, content: prev.content + tag }));
    } else if (mergeTagsTarget === 'quick_sms') {
      setOneToOneMessage(prev => prev + tag);
    }
    setShowMergeTags(false);
  };

  // Template sorting functions
  const handleTemplateSort = (field: 'name' | 'content' | 'createdAt') => {
    if (templateSortField === field) {
      setTemplateSortDirection(templateSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTemplateSortField(field);
      setTemplateSortDirection('asc');
    }
  };

  const sortTemplates = (templatesArray: SmsTemplate[]) => {
    if (!templateSortField) return templatesArray;

    return [...templatesArray].sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      switch (templateSortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'content':
          aValue = a.content.toLowerCase();
          bValue = b.content.toLowerCase();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return templateSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return templateSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableTableHead = ({ 
    field, 
    label, 
    className = "" 
  }: { 
    field: 'name' | 'content' | 'createdAt'; 
    label: string; 
    className?: string;
  }) => {
    const isSorted = templateSortField === field;
    return (
      <TableHead 
        className={`cursor-pointer select-none hover:bg-muted/50 ${className}`}
        onClick={() => handleTemplateSort(field)}
        data-testid={`sort-${field}`}
      >
        <div className="flex items-center gap-2">
          {label}
          {isSorted ? (
            templateSortDirection === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          ) : (
            <div className="flex flex-col h-4 w-4 opacity-40">
              <ChevronUp className="h-2.5 w-4 -mb-1" />
              <ChevronDown className="h-2.5 w-4" />
            </div>
          )}
        </div>
      </TableHead>
    );
  };

  // Contact interface for typing
  interface Contact {
    id: string;
    name: string;
    phone: string;
    email: string;
    type: 'client' | 'prospect';
    company?: string;
  }

  // Filter and sort contacts
  const filteredContacts = contacts
    .filter((contact: Contact) => {
      const matchesType = typeFilter === 'all' || contact.type === typeFilter;
      const matchesSearch = !searchFilter || 
        contact.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        contact.phone.includes(searchFilter) ||
        contact.email.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (contact.company && contact.company.toLowerCase().includes(searchFilter.toLowerCase()));
      return matchesType && matchesSearch;
    })
    .sort((a: Contact, b: Contact) => {
      if (!sortField) return 0;
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Helper function to get selected contacts data
  const getSelectedContactsData = (): Contact[] => {
    return filteredContacts.filter(contact => selectedContacts.has(contactKey(contact)));
  };

  // Helper to get contact name from recipientId (e.g., "client-123" -> "Acme Corp")
  const getRecipientName = (recipientId: string): string => {
    const [type, id] = recipientId.split('-');
    const numId = parseInt(id);
    
    if (type === 'client') {
      const client = (clients as any[])?.find(c => c.id === numId);
      return client?.name || recipientId;
    } else if (type === 'prospect') {
      const prospect = (prospects as any[])?.find(p => p.id === numId);
      return prospect?.name || recipientId;
    }
    
    return recipientId;
  };

  // Paginate contacts
  const totalContacts = filteredContacts.length;
  const totalPages = Math.ceil(totalContacts / contactsPerPage);
  const paginatedContacts = filteredContacts.slice(
    (currentPage - 1) * contactsPerPage,
    currentPage * contactsPerPage
  );

  // Filter saved lists
  const filteredLists = savedLists.filter(list =>
    !listSearchFilter ||
    list.name.toLowerCase().includes(listSearchFilter.toLowerCase()) ||
    (list.description && list.description.toLowerCase().includes(listSearchFilter.toLowerCase()))
  );

  // Helper functions for contact management
  const handleSort = (field: 'name' | 'phone' | 'type') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const contactKey = (contact: Contact) => `${contact.type}-${contact.id}`;

  const toggleSelectContact = (contact: Contact) => {
    const key = contactKey(contact);
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedContacts(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedContacts.size === paginatedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(paginatedContacts.map((c: Contact) => contactKey(c))));
    }
  };

  const handleSaveList = () => {
    if (!listName) {
      toast({
        title: 'Error',
        description: 'Please enter a list name.',
        variant: 'destructive',
      });
      return;
    }

    const contactIdsArray = Array.from(selectedContacts);

    saveListMutation.mutate({
      name: listName,
      description: listDescription,
      contactIds: contactIdsArray,
      contactCount: contactIdsArray.length,
    });
  };

  // Mutations
  const createTemplateMutation = useMutation({
    mutationFn: async (data: { name: string; content: string; folderId?: number | null }) => {
      return apiRequest('POST', '/api/sms/templates', data);
    },
    onSuccess: () => {
      // Invalidate and refetch templates
      queryClient.invalidateQueries({ queryKey: ['/api/sms/templates'] });
      queryClient.refetchQueries({ queryKey: ['/api/sms/templates'] });
      setNewTemplateName('');
      setNewTemplateContent('');
      setIsCreateTemplateDialogOpen(false);
      toast({
        title: 'Template Created',
        description: 'SMS template created successfully.',
      });
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Failed to create SMS template.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Save contact list mutation
  const saveListMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; contactIds: string[]; contactCount: number }) => {
      return apiRequest('POST', '/api/sms/lists', data);
    },
    onSuccess: () => {
      setListName('');
      setListDescription('');
      setIsSaveListDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['/api/sms/lists'] });
      toast({
        title: "List saved",
        description: "Contact list has been saved successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Error saving list:', error);
      const errorMessage = error.message || 'Failed to save contact list. Please try again.';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const updateListMutation = useMutation({
    mutationFn: async ({ id, ...listData }: { id: number; name: string; description?: string; contactIds: string[]; contactCount: number }) => {
      const response = await apiRequest('PUT', `/api/sms/lists/${id}`, listData);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/lists'] });
      setIsEditListDialogOpen(false);
      setListToEdit(null);
      setEditListName('');
      setEditListDescription('');
      setEditListSelectedContacts(new Set());
      toast({
        title: "List updated",
        description: "Contact list has been updated successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Error updating list:', error);
      const errorMessage = error.message || 'Failed to update contact list. Please try again.';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const deleteListMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/sms/lists/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/lists'] });
      setListToDelete(null);
      setListDeleteDialogOpen(false);
      toast({
        title: "List deleted",
        description: "Contact list has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting list:', error);
      const errorMessage = error.message || 'Failed to delete contact list. Please try again.';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name: string; content: string; folderId?: string }) => {
      return apiRequest('PUT', `/api/sms/templates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/templates'] });
      setIsEditTemplateDialogOpen(false);
      setSelectedTemplateForEdit(null);
      setEditTemplateForm({ name: "", content: "" });
      toast({
        title: 'Template Updated',
        description: 'SMS template updated successfully.',
      });
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Failed to update SMS template.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest('DELETE', `/api/sms/templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/templates'] });
      setTemplateToDelete(null);
      toast({
        title: "Template deleted",
        description: "SMS template has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting template:', error);
      const errorMessage = error.message || 'Failed to delete SMS template. Please try again.';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const sendOneToOneMutation = useMutation({
    mutationFn: async (data: { to: string; message: string; clientId?: number; prospectId?: number }) => {
      return apiRequest('POST', '/api/sms/send-one-to-one', {
        ...data,
        to: toE164Format(data.to)
      });
    },
    onSuccess: () => {
      setOneToOnePhone('');
      setOneToOneMessage('');
      setSmsError(null);
      setBulkSendResult(null);
      setIsQuickSmsDialogOpen(false);
      toast({
        title: 'SMS Sent',
        description: 'One-to-one SMS sent successfully.',
      });
    },
    onError: (error: any) => {
      // Show detailed error message in modal
      const errorMessage = error.message || 'Failed to send SMS.';
      setSmsError(errorMessage);
    },
  });

  const sendBulkSmsMutation = useMutation({
    mutationFn: async (data: { recipientIds: string[]; message: string; fromNumber?: string; fromNumberId?: number }) => {
      return apiRequest('POST', '/api/sms/bulk-send', data);
    },
    onSuccess: async (response: Response) => {
      const result = await response.json();
      
      // If validation failed (no messages sent), show errors in contacts tab
      if (result.failedCount > 0 && result.sentCount === 0 && !result.success) {
        setBulkSendResult(result);
        setOneToOneMessage('');
        setIsQuickSmsDialogOpen(false);
        
        // Switch to contacts tab to show validation errors
        setActiveTab('contacts');
        
        toast({
          title: 'Validation Failed',
          description: `${result.failedCount} recipient(s) failed validation. No messages were sent. Check the error card below.`,
          variant: 'destructive',
        });
        return;
      }
      
      // All successful
      setBulkSendResult(null);
      setSmsError(null);
      setOneToOneMessage('');
      setIsQuickSmsDialogOpen(false);
      setSelectedContacts(new Set());
      queryClient.invalidateQueries({ queryKey: ['/api/sms/analytics/campaigns'] });
      
      toast({
        title: 'Bulk SMS Sent Successfully',
        description: `✅ Successfully sent ${result.sentCount} SMS message(s)${result.skippedCount > 0 ? ` (${result.skippedCount} skipped - DNC list)` : ''}`,
      });
    },
    onError: (error: any) => {
      // Show detailed error message in modal
      const errorMessage = error.message || 'Failed to send bulk SMS';
      setSmsError(errorMessage);
    },
  });

  const addToDncMutation = useMutation({
    mutationFn: async (data: { phone: string; reason: string }) => {
      return apiRequest('POST', '/api/sms/dnc', {
        phone: toE164Format(data.phone),
        reason: data.reason
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/dnc'] });
      setDncPhone('');
      setDncReason('');
      toast({
        title: 'Added to DNC',
        description: 'Phone number added to Do Not Contact list.',
      });
    },
    onError: (error: any) => {
      // Show detailed validation error message
      const errorMessage = error.message || 'Failed to add to DNC list.';
      toast({
        title: 'Failed to Add to DNC',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const removeFromDncMutation = useMutation({
    mutationFn: async (id: string | number) => {
      return apiRequest('DELETE', `/api/sms/dnc/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/dnc'] });
      setDncEntryToDelete(null);
      toast({
        title: 'Removed from DNC',
        description: 'Phone number removed from Do Not Contact list.',
      });
    },
    onError: (error: any) => {
      const errorMessage = error.message || 'Failed to remove from DNC list.';
      toast({
        title: 'Failed to Remove from DNC',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: async (folder: { name: string; description?: string }) => {
      const response = await apiRequest('POST', '/api/sms/folders', folder);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/folders'] });
      setIsFolderDialogOpen(false);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder created successfully" });
    },
    onError: (error) => {
      console.error('Error creating folder:', error);
      toast({ 
        title: "Error creating folder", 
        description: error.message || "Failed to create folder",
        variant: "destructive"
      });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, ...folder }: { id: string; name: string; description?: string }) => {
      return apiRequest('PUT', `/api/sms/folders/${id}`, folder);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/folders'] });
      setIsFolderDialogOpen(false);
      setSelectedFolder(null);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder updated successfully" });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest('DELETE', `/api/sms/folders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/folders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/sms/templates'] });
      setFolderToDelete(null);
      toast({ title: "Folder deleted successfully" });
    },
  });

  const moveTemplateMutation = useMutation({
    mutationFn: async ({ templateId, folderId }: { templateId: string; folderId: string | null }) => {
      return apiRequest('PUT', `/api/sms/templates/${templateId}`, { folderId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/sms/templates'] });
      setIsMoveDialogOpen(false);
      setSelectedTemplateForMove(null);
      toast({ title: "Template moved successfully" });
    },
  });

  // Folder handler functions
  const handleCreateFolder = () => {
    if (!folderForm.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Folder name is required",
        variant: "destructive"
      });
      return;
    }
    createFolderMutation.mutate(folderForm);
  };

  const handleUpdateFolder = () => {
    if (selectedFolder) {
      updateFolderMutation.mutate({
        id: String(selectedFolder.id),
        ...folderForm,
      });
    }
  };

  const handleEditFolder = (folder: SmsTemplateFolder) => {
    setSelectedFolder(folder);
    setFolderForm({
      name: folder.name,
      description: folder.description || "",
    });
    setIsFolderDialogOpen(true);
  };

  const handleDeleteFolder = (folder: SmsTemplateFolder) => {
    setFolderToDelete(folder);
  };

  const handleDeleteTemplate = (template: SmsTemplate) => {
    setTemplateToDelete(template);
  };

  const handleDeleteList = (list: SmsListType) => {
    setListToDelete(list);
  };

  const handleDeleteDncEntry = (entry: DNCEntry) => {
    setDncEntryToDelete(entry);
  };

  const handleMoveTemplate = (template: SmsTemplate) => {
    setSelectedTemplateForMove(template);
    setSelectedMoveFolder(null);
    setIsMoveDialogOpen(true);
  };

  const handleConfirmMoveTemplate = (folderId: string | null) => {
    if (selectedTemplateForMove) {
      moveTemplateMutation.mutate({
        templateId: String(selectedTemplateForMove.id),
        folderId,
      });
    }
  };

  const handleEditTemplate = (template: SmsTemplate) => {
    setSelectedTemplateForEdit(template);
    setEditTemplateForm({
      name: template.name,
      content: template.content,
    });
    setIsEditTemplateDialogOpen(true);
  };

  const handleUpdateTemplate = () => {
    if (!editTemplateForm.name || !editTemplateForm.content) {
      toast({
        title: 'Error',
        description: 'Please fill in all template fields.',
        variant: 'destructive',
      });
      return;
    }

    if (editTemplateForm.content.length > 1600) {
      toast({
        title: 'Error',
        description: 'SMS content cannot exceed 1600 characters.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedTemplateForEdit) {
      updateTemplateMutation.mutate({
        id: String(selectedTemplateForEdit.id),
        name: editTemplateForm.name,
        content: editTemplateForm.content,
      });
    }
  };

  const handleSendOneToOne = () => {
    // Clear any previous errors and results
    setSmsError(null);
    setBulkSendResult(null);
    
    // Check if we're doing bulk send (selected contacts) or one-to-one
    const selectedData = getSelectedContactsData();
    const isBulkSend = selectedData.length > 0;

    if (isBulkSend) {
      // Bulk send
      if (!oneToOneMessage) {
        toast({
          title: 'Error',
          description: 'Please provide a message.',
          variant: 'destructive',
        });
        return;
      }

      const recipientIds = selectedData.map(contact => contact.id);

      // Get the selected from number
      const selectedPhone = selectedFromNumberId
        ? smsPhoneNumbers.find(p => String(p.id) === selectedFromNumberId)
        : smsPhoneNumbers.find(p => p.isDefault) || smsPhoneNumbers[0];

      sendBulkSmsMutation.mutate({
        recipientIds,
        message: oneToOneMessage,
        ...(selectedPhone && { fromNumber: selectedPhone.phoneNumber, fromNumberId: selectedPhone.id }),
      });
    } else {
      // One-to-one send
      if (!oneToOnePhone || !oneToOneMessage) {
        toast({
          title: 'Error',
          description: 'Please enter phone number and message.',
          variant: 'destructive',
        });
        return;
      }

      const mutationData: any = {
        to: oneToOnePhone,
        message: oneToOneMessage,
      };

      // Include clientId or prospectId if a contact was selected
      if (selectedQuickSmsClient && selectedQuickSmsClientType) {
        if (selectedQuickSmsClientType === 'client') {
          mutationData.clientId = selectedQuickSmsClient.id;
        } else if (selectedQuickSmsClientType === 'prospect') {
          mutationData.prospectId = selectedQuickSmsClient.id;
        }
      }

      sendOneToOneMutation.mutate(mutationData);
    }
  };

  const handleAddToDnc = () => {
    if (!dncPhone) {
      toast({
        title: 'Error',
        description: 'Please enter a phone number.',
        variant: 'destructive',
      });
      return;
    }

    addToDncMutation.mutate({
      phone: dncPhone,
      reason: dncReason || 'manual',
    });
  };



  return (
    <div className="space-y-6">
      <MarketingPageHeader
        icon={MessageSquare}
        iconColor="bg-green-500"
        title="SMS Marketing"
        description="Manage SMS campaigns, templates, and messaging"
        actions={
          <PermissionButton
            permission="marketing:edit"
            whenDenied="hide"
            onClick={() => {
              if (!isSmsReady) return;
              // Clear selected contacts for one-to-one SMS
              setSelectedContacts(new Set());
              setIsQuickSmsDialogOpen(true);
            }}
            className="flex items-center gap-2"
            disabled={!isSmsReady}
            title={!isSmsReady ? "SMS sending not configured. Go to Settings to set up Phone & SMS." : undefined}
          >
            <Send className="h-4 w-4" />
            Send Quick SMS
          </PermissionButton>
        }
      />

      {/* SMS Not Configured Alert */}
      {!isSmsReady && (
        <Alert variant="default" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertTitle>SMS Sending Not Configured</AlertTitle>
          <AlertDescription>
            To send SMS messages, you need to configure Phone & SMS in your settings.{" "}
            <Link href="/settings?tab=phone" className="font-medium underline hover:no-underline">
              Go to Settings &rarr; Phone & SMS
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {/* Throttling Status */}
      {throttling && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              SMS Usage Limits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Daily Usage</Label>
                <div className="flex items-center gap-2">
                  <Progress value={((throttling?.dailyCount || 0) / 100) * 100} className="flex-1" />
                  <span className="text-sm text-muted-foreground">{throttling?.dailyCount || 0}/100</span>
                </div>
              </div>
              <div>
                <Label>Monthly Usage</Label>
                <div className="flex items-center gap-2">
                  <Progress value={((throttling?.monthlyCount || 0) / 1000) * 100} className="flex-1" />
                  <span className="text-sm text-muted-foreground">{throttling?.monthlyCount || 0}/1000</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 bg-[#eff6fe] dark:bg-gray-800">
          <TabsTrigger value="templates" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <Edit className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <Users className="h-4 w-4" />
            Lists
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="dnc" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <ShieldX className="h-4 w-4" />
            DNC List
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <SectionHeader
            title="SMS Templates"
            actions={
              <>
              <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
                <DialogTrigger asChild>
                  <PermissionButton
                    permission="marketing:edit"
                    whenDenied="hide"
                    variant="outline"
                    onClick={() => {
                      setSelectedFolder(null);
                      setFolderForm({ name: "", description: "" });
                    }}
                  >
                    <FolderPlus className="mr-2 h-4 w-4" />
                    Add Folder
                  </PermissionButton>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {selectedFolder ? 'Edit Folder' : 'Create Folder'}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedFolder ? 'Update folder details' : 'Create a new folder to organize your templates'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="folderName">Folder Name</Label>
                      <Input
                        id="folderName"
                        value={folderForm.name}
                        onChange={(e) => setFolderForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter folder name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="folderDescription">Description (Optional)</Label>
                      <Textarea
                        id="folderDescription"
                        value={folderForm.description}
                        onChange={(e) => setFolderForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Enter folder description"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setIsFolderDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={selectedFolder ? handleUpdateFolder : handleCreateFolder}
                      disabled={createFolderMutation.isPending || updateFolderMutation.isPending || !folderForm.name.trim()}
                    >
                      {createFolderMutation.isPending || updateFolderMutation.isPending ? 'Processing...' : (selectedFolder ? 'Update Folder' : 'Create Folder')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={isCreateTemplateDialogOpen} onOpenChange={setIsCreateTemplateDialogOpen}>
                <DialogTrigger asChild>
                  <PermissionButton
                    permission="marketing:edit"
                    whenDenied="hide"
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Create Template
                  </PermissionButton>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create SMS Template</DialogTitle>
                    <DialogDescription>
                      Create a reusable SMS template with merge fields
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="templateName">Template Name</Label>
                      <Input
                        id="templateName"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        placeholder="Enter template name"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="templateContent">Template Content</Label>
                        <Dialog open={showMergeTags} onOpenChange={setShowMergeTags}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setMergeTagsTarget('create');
                                setShowMergeTags(true);
                              }}
                            >
                              <Tag className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle>Insert Merge Tag</DialogTitle>
                              <DialogDescription>
                                Select a merge tag to insert into your template
                              </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-2">
                              {mergeTags.map((tag) => (
                                <Button
                                  key={tag.value}
                                  variant="outline"
                                  className="justify-start"
                                  onClick={() => insertMergeTag(tag.value)}
                                >
                                  {tag.label} - {tag.value}
                                </Button>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                      <div className="mt-2">
                        <Textarea
                        id="templateContent"
                        value={newTemplateContent}
                        onChange={(e) => setNewTemplateContent(e.target.value)}
                        placeholder="Enter template content with merge fields like {{first_name}}"
                        rows={4}
                        />
                      </div>
                      <div className={`text-sm mt-1 ${newTemplateContent.length > 160 ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {newTemplateContent.length}/160 characters
                      </div>
                    </div>
                    {currentFolderView && (
                      <div>
                        <Label>Folder</Label>
                        <p className="text-sm text-muted-foreground">
                          Will be created in: {currentFolderView.name}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsCreateTemplateDialogOpen(false);
                        setNewTemplateName('');
                        setNewTemplateContent('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => {
                        // Validate folderId exists before creating to prevent orphaned templates
                        const validFolderIds = new Set(folders.map(f => Number(f.id)));
                        const targetFolderId = folderId && validFolderIds.has(Number(folderId)) 
                          ? folderId 
                          : null;
                        
                        createTemplateMutation.mutate({
                          name: newTemplateName,
                          content: newTemplateContent,
                          folderId: targetFolderId
                        });
                      }}
                      disabled={createTemplateMutation.isPending || !newTemplateName.trim() || !newTemplateContent.trim()}
                    >
                      {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </>
            }
          />

          <div className="space-y-6">
            <FolderNavigation
              currentFolder={currentFolderView}
              onBackClick={() => setFolderId(null)}
            />

            {/* Folder View - Show templates inside selected folder */}
            {currentFolderView ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 py-4">
                    <div className="flex items-center gap-2">
                      <Folder className="h-5 w-5 text-blue-600" />
                      <h4 className="text-lg font-medium">{currentFolderView.name}</h4>
                      <Badge variant="secondary">
                        {templates.filter(t => Number(t.folderId) === Number(currentFolderView.id)).length} templates
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditFolder(currentFolderView)}
                      >
                        <Edit className="h-4 w-4" />
                        Edit Folder
                      </Button>
                    </div>
                  </CardHeader>
                  {currentFolderView.description && (
                    <p className="text-sm text-muted-foreground mb-4 px-6">{currentFolderView.description}</p>
                  )}
                </Card>

                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead field="name" label="Name" />
                        <SortableTableHead field="content" label="Content" />
                        <SortableTableHead field="createdAt" label="Created" />
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortTemplates(getTemplatesInFolder(currentFolderView.id))
                        .map((template: SmsTemplate) => (
                        <TableRow key={template.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium">{template.name}</TableCell>
                          <TableCell className="max-w-md truncate">
                            {template.content.length > 80 
                              ? `${template.content.substring(0, 80)}...` 
                              : template.content
                            }
                          </TableCell>
                          <TableCell>
                            {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : 'Unknown'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMoveTemplate(template)}
                                className="text-muted-foreground hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-move-${template.id}`}
                              >
                                <MoveHorizontal className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEditTemplate(template)}
                                className="text-muted-foreground hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-edit-${template.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDeleteTemplate(template)}
                                disabled={deleteTemplateMutation.isPending}
                                className="text-red-600 hover:!text-white hover:!bg-red-600"
                                data-testid={`button-delete-${template.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                {templates.filter(t => Number(t.folderId) === Number(currentFolderView.id)).length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No templates in this folder yet.
                  </p>
                )}
              </div>
            ) : (
              /* Main View - Show folders and unorganized templates */
              <div className="space-y-6">
                {/* Folders Section */}
                {folders.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-md font-medium text-muted-foreground">Folders</h4>
                    <div className="grid gap-3">
                      {folders.map((folder) => {
                        const folderTemplateCount = getTemplatesInFolder(folder.id).length;
                        return (
                          <Card key={folder.id} className="cursor-pointer hover:bg-accent/50 transition-colors">
                            <CardHeader className="pb-3">
                              <div
                                className="flex items-center justify-between"
                                onClick={() => setFolderId(folder.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                    <Folder className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-medium">{folder.name}</h3>
                                    <p className="text-sm text-muted-foreground">
                                      {folderTemplateCount} template{folderTemplateCount !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditFolder(folder);
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFolder(folder);
                                    }}
                                    disabled={deleteFolderMutation.isPending}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Unorganized Templates Section */}
                {(() => {
                  const unorganizedTemplates = getUnorganizedTemplates();
                  if (unorganizedTemplates.length === 0 && folders.length > 0) return null;
                  
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h4 className="text-md font-medium text-muted-foreground">
                          SMS Templates
                        </h4>
                        <Badge variant="outline">{unorganizedTemplates.length}</Badge>
                      </div>
                      <Card>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <SortableTableHead field="name" label="Name" />
                              <SortableTableHead field="content" label="Content" />
                              <SortableTableHead field="createdAt" label="Created" />
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortTemplates(unorganizedTemplates).map((template: SmsTemplate) => (
                              <TableRow key={template.id} className="hover:bg-muted/50">
                                <TableCell className="font-medium">
                                  {template.name}
                                </TableCell>
                                <TableCell className="max-w-md truncate">
                                  {template.content.length > 80 
                                    ? `${template.content.substring(0, 80)}...` 
                                    : template.content
                                  }
                                </TableCell>
                                <TableCell>
                                  {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : 'Unknown'}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end space-x-2">
                                    {folders.length > 0 && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleMoveTemplate(template)}
                                        className="text-muted-foreground hover:!text-white hover:!bg-[#1d4c99]"
                                        data-testid={`button-move-${template.id}`}
                                      >
                                        <MoveHorizontal className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleEditTemplate(template)}
                                      className="text-muted-foreground hover:!text-white hover:!bg-[#1d4c99]"
                                      data-testid={`button-edit-${template.id}`}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleDeleteTemplate(template)}
                                      disabled={deleteTemplateMutation.isPending}
                                      className="text-red-600 hover:!text-white hover:!bg-red-600"
                                      data-testid={`button-delete-${template.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Card>
                    </div>
                  );
                })()}

                {templates.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No SMS templates created yet. Click "Create Template" to get started.
                  </p>
                )}
              </div>
            )}
          </div>
        </TabsContent>


        {/* Lists Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <SectionHeader
            title="Contact Lists"
            actions={
              <div className="flex items-center space-x-2">
              <Badge variant="outline" className="text-sm">
                {filteredContacts.length} of {contacts.length} contacts
              </Badge>
              {totalPages > 1 && (
                <Badge variant="secondary" className="text-sm">
                  Page {currentPage} of {totalPages}
                </Badge>
              )}
              </div>
            }
          />

          {savedLists.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Smart Lists ({savedLists.length})</CardTitle>
                    <CardDescription className="text-sm">
                      Click to load saved filters
                    </CardDescription>
                  </div>
                  <Input
                    placeholder="Search lists..."
                    value={listSearchFilter}
                    onChange={(e) => setListSearchFilter(e.target.value)}
                    className="w-48"
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {savedLists
                    .filter(list => 
                      listSearchFilter === '' || 
                      list.name.toLowerCase().includes(listSearchFilter.toLowerCase()) ||
                      (list.description && list.description.toLowerCase().includes(listSearchFilter.toLowerCase()))
                    )
                    .map((list) => (
                    <div key={list.id} className="flex items-center justify-between p-2 rounded border-l-4 border-l-blue-500 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{list.name}</span>
                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5">
                            {list.contactCount}
                          </Badge>
                        </div>
                        {list.description && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{list.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Select the specific contacts from the list
                            const contactKeys = new Set(list.contactIds);
                            setSelectedContacts(contactKeys);
                            toast({ 
                              title: `Loaded "${list.name}"`, 
                              description: `${list.contactCount} contact${list.contactCount !== 1 ? 's' : ''} selected`
                            });
                          }}
                          className="h-7 px-2 text-xs"
                        >
                          Load
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setListToEdit(list);
                            setEditListName(list.name);
                            setEditListDescription(list.description || '');
                            setEditListSelectedContacts(new Set(list.contactIds || []));
                            setIsEditListDialogOpen(true);
                          }}
                          className="h-7 px-1 text-xs text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setListToDelete(list);
                            setListDeleteDialogOpen(true);
                          }}
                          className="h-7 px-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Validation Error Card */}
          {bulkSendResult && bulkSendResult.errors && bulkSendResult.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="flex items-center justify-between">
                <span>Bulk SMS Validation Failed</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBulkSendResult(null)}
                  className="h-6 px-2 text-xs"
                >
                  Dismiss
                </Button>
              </AlertTitle>
              <AlertDescription className="mt-3 space-y-3">
                <p>
                  {bulkSendResult.failedCount} recipient(s) failed validation. No messages were sent.
                </p>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium">Failed Recipients:</div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {bulkSendResult.errors.map((err: any, idx: number) => (
                      <div key={idx} className="p-3 bg-destructive/10 rounded border border-destructive/20 text-sm">
                        <div className="font-medium text-foreground">{getRecipientName(err.recipientId)}</div>
                        <div className="text-muted-foreground text-xs mt-1">{err.error}</div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {bulkSendResult.skippedCount > 0 && (
                  <div className="text-xs text-yellow-600 dark:text-yellow-500 mt-2 pt-2 border-t border-destructive/20">
                    ⏭️ {bulkSendResult.skippedCount} contact(s) skipped (on DNC list)
                  </div>
                )}
                
                <div className="text-xs mt-3 pt-2 border-t border-destructive/20">
                  Fix the issues above and try sending again.
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Available Contacts</CardTitle>
              <CardDescription>
                Contacts from your clients and prospects database (DNC filtered)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <ContactListFilters
                  typeFilter={typeFilter}
                  onTypeFilterChange={setTypeFilter}
                  searchFilter={searchFilter}
                  onSearchFilterChange={setSearchFilter}
                  contactType="sms"
                  totalContacts={contacts.length}
                  filteredContacts={filteredContacts.length}
                  currentPage={currentPage}
                  totalPages={totalPages}
                />
              </div>

              <BulkActionsBar
                selectedCount={selectedContacts.size}
                selectedContactsPreview={getSelectedContactsData().map(c => c.name).join(", ").substring(0, 50) + (getSelectedContactsData().map(c => c.name).join(", ").length > 50 ? "..." : "")}
                onSend={() => {
                  // Open quick SMS dialog for bulk sending
                  setIsQuickSmsDialogOpen(true);
                  setOneToOneMessage('');
                }}
                onAddToDNC={async () => {
                  const selectedData = getSelectedContactsData();
                  if (selectedData.length === 0) return;

                  try {
                    const results = await Promise.allSettled(
                      selectedData.map(contact =>
                        apiRequest('POST', '/api/sms/dnc', {
                          phone: toE164Format(contact.phone),
                          reason: "manual"
                        }).then(() => ({ contact, success: true }))
                          .catch((error) => ({ contact, success: false, error: error.message || 'Failed' }))
                      )
                    );

                    const successes = results.filter(r => r.status === 'fulfilled' && r.value.success);
                    const failures = results
                      .filter((r): r is PromiseFulfilledResult<{ contact: any; success: false; error: string }> => 
                        r.status === 'fulfilled' && !r.value.success
                      )
                      .map(r => r.value);

                    queryClient.invalidateQueries({ queryKey: ['/api/sms/dnc'] });
                    queryClient.invalidateQueries({ queryKey: ['/api/sms/contacts'] });
                    setSelectedContacts(new Set());

                    if (failures.length === 0) {
                      toast({
                        title: "Success",
                        description: `${successes.length} contact${successes.length !== 1 ? 's' : ''} added to DNC list`,
                      });
                    } else {
                      // Show detailed errors
                      const errorDetails = failures
                        .slice(0, 3)
                        .map(f => `${f.contact.name}: ${f.error}`)
                        .join('\n');
                      
                      const moreErrors = failures.length > 3 ? `\n...and ${failures.length - 3} more` : '';
                      
                      toast({
                        title: failures.length === results.length ? "Failed to Add to DNC" : "Partial Success",
                        description: failures.length === results.length 
                          ? `All ${failures.length} contact(s) failed:\n${errorDetails}${moreErrors}`
                          : `${successes.length} added, ${failures.length} failed:\n${errorDetails}${moreErrors}`,
                        variant: failures.length === results.length ? "destructive" : "default",
                      });
                    }
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description: error.message || "Failed to add contacts to DNC list",
                      variant: "destructive"
                    });
                  }
                }}
                onSaveList={() => {
                  setIsSaveListDialogOpen(true);
                }}
                sendLabel="Send SMS"
                contactType="sms"
                sendDisabled={!isSmsReady}
                sendDisabledTooltip="SMS sending not configured. Go to Settings to set up Phone & SMS."
              />

              <SaveListDialog
                isOpen={isSaveListDialogOpen}
                onOpenChange={setIsSaveListDialogOpen}
                listName={listName}
                onListNameChange={setListName}
                listDescription={listDescription}
                onListDescriptionChange={setListDescription}
                contactCount={selectedContacts.size}
                onSave={handleSaveList}
                onCancel={() => setIsSaveListDialogOpen(false)}
                isLoading={saveListMutation.isPending}
                disabled={false}
              />

              {selectedContacts.size > 0 && (
                <div className="flex justify-end mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedContacts(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              <ContactListTable
                contacts={contacts.map(c => ({ ...c, phone: c.phone || '', email: c.email || '' }))}
                selectedContacts={selectedContacts}
                onToggleSelect={(contact) => toggleSelectContact(contact as Contact)}
                onToggleSelectAll={toggleSelectAll}
                sortField={sortField || null}
                sortDirection={sortDirection}
                onSort={(field) => handleSort(field as 'name' | 'phone' | 'type')}
                contactType="sms"
                paginatedContacts={paginatedContacts.map(c => ({ ...c, phone: c.phone || '', email: c.email || '' }))}
                allSelected={selectedContacts.size === paginatedContacts.length && paginatedContacts.length > 0}
              />
              
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={(currentPage - 1) * contactsPerPage}
                endIndex={Math.min(currentPage * contactsPerPage, filteredContacts.length)}
                totalItems={filteredContacts.length}
                onPageChange={setCurrentPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <SectionHeader title="SMS Analytics" />
          
          {!analytics || !analytics.overview ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                Loading analytics...
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Overview Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                      Total Sent
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.overview.totalSent}</div>
                    <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Delivered
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{analytics.overview.delivered}</div>
                    <p className="text-xs text-muted-foreground mt-1">{analytics.overview.deliveryRate} delivery rate</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Clock className="h-4 w-4 text-orange-600" />
                      Pending
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">{analytics.overview.pending}</div>
                    <p className="text-xs text-muted-foreground mt-1">In transit</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      Failed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">{analytics.overview.failed}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {analytics.overview.totalSent > 0 
                        ? `${((analytics.overview.failed / analytics.overview.totalSent) * 100).toFixed(1)}% failure rate`
                        : 'No failures'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Delivery Timeline Chart */}
              {analytics.timeline && analytics.timeline.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Delivery Timeline (Last 30 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 flex items-end justify-between gap-2">
                      {analytics.timeline.map((day, index) => {
                        const maxValue = Math.max(...analytics.timeline.map(d => d.sent));
                        const sentHeight = maxValue > 0 ? (day.sent / maxValue) * 100 : 0;
                        const deliveredHeight = maxValue > 0 ? (day.delivered / maxValue) * 100 : 0;
                        const failedHeight = maxValue > 0 ? (day.failed / maxValue) * 100 : 0;
                        
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center gap-1">
                            <div className="w-full flex flex-col items-center gap-0.5 h-48">
                              {day.sent > 0 && (
                                <div 
                                  className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600 relative group"
                                  style={{ height: `${sentHeight}%` }}
                                >
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                                    Sent: {day.sent}
                                  </div>
                                </div>
                              )}
                              {day.delivered > 0 && (
                                <div 
                                  className="w-full bg-green-500 transition-all hover:bg-green-600 relative group"
                                  style={{ height: `${deliveredHeight}%` }}
                                >
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                                    Delivered: {day.delivered}
                                  </div>
                                </div>
                              )}
                              {day.failed > 0 && (
                                <div 
                                  className="w-full bg-red-500 rounded-b transition-all hover:bg-red-600 relative group"
                                  style={{ height: `${failedHeight}%` }}
                                >
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                                    Failed: {day.failed}
                                  </div>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground rotate-45 origin-left whitespace-nowrap">
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded"></div>
                        <span className="text-sm">Sent</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded"></div>
                        <span className="text-sm">Delivered</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded"></div>
                        <span className="text-sm">Failed</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Failure Analysis */}
              {analytics.failureReasons && analytics.failureReasons.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      Failure Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Error Code</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.failureReasons
                          .sort((a, b) => b.count - a.count)
                          .map((reason, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Badge variant="destructive">{reason.errorCode}</Badge>
                            </TableCell>
                            <TableCell className="font-medium">{reason.message}</TableCell>
                            <TableCell className="text-right">{reason.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent SMS Activity</CardTitle>
                  <div className="flex flex-col gap-3 mt-4">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          placeholder="Search by name, phone, email, or message..."
                          value={analyticsSearchFilterLocal}
                          onChange={(e) => setAnalyticsSearchFilterLocal(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <Select value={analyticsStatusFilterLocal} onValueChange={setAnalyticsStatusFilterLocal}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="queued">Queued</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">From Date</Label>
                        <Input
                          type="date"
                          value={analyticsDateRangeLocal.start}
                          onChange={(e) => setAnalyticsDateRangeLocal(prev => ({ ...prev, start: e.target.value }))}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">To Date</Label>
                        <Input
                          type="date"
                          value={analyticsDateRangeLocal.end}
                          onChange={(e) => setAnalyticsDateRangeLocal(prev => ({ ...prev, end: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={applyAnalyticsFilters} size="sm">
                        Apply Filters
                      </Button>
                      <Button onClick={clearAnalyticsFilters} variant="outline" size="sm">
                        Clear All
                      </Button>
                      {(analyticsStatusFilter !== 'all' || analyticsSearchFilter !== '' || 
                        analyticsDateRange.start !== new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ||
                        analyticsDateRange.end !== new Date().toISOString().split('T')[0]) && (
                        <Badge variant="secondary" className="ml-2">
                          Filters Active
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!analytics.recentActivity || analytics.recentActivity.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No recent SMS activity</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Recipient</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Message Preview</TableHead>
                          <TableHead>Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.recentActivity.slice(0, 50).map((analytic: SmsAnalytic) => (
                          <TableRow key={analytic.id}>
                            <TableCell>
                              <Badge variant={
                                analytic.event === 'sent' ? 'secondary' :
                                analytic.event === 'delivered' ? 'default' :
                                analytic.event === 'failed' ? 'destructive' :
                                'outline'
                              }>
                                {analytic.event}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {analytic.recipientName || 'Unknown'}
                                </span>
                                {analytic.recipientEmail && (
                                  <span className="text-xs text-muted-foreground">{analytic.recipientEmail}</span>
                                )}
                                {analytic.recipientType && (
                                  <Badge variant="outline" className="text-xs w-fit mt-1">
                                    {analytic.recipientType}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{analytic.recipientPhone}</TableCell>
                            <TableCell className="max-w-xs">
                              <button
                                onClick={() => {
                                  setSelectedMessage(analytic);
                                  setMessagePreviewOpen(true);
                                }}
                                className="text-sm text-muted-foreground hover:text-primary hover:underline cursor-pointer text-left w-full block truncate"
                                title="Click to view full message"
                              >
                                {analytic.messagePreview 
                                  ? (analytic.messagePreview.length > 50 
                                      ? analytic.messagePreview.substring(0, 50) + '...' 
                                      : analytic.messagePreview)
                                  : 'No preview'}
                              </button>
                            </TableCell>
                            <TableCell className="text-sm">
                              {analytic.timestamp ? new Date(analytic.timestamp).toLocaleString() : 'Unknown'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Message Preview Modal */}
        <Dialog open={messagePreviewOpen} onOpenChange={setMessagePreviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Message Details</DialogTitle>
              <DialogDescription>
                Full message content and delivery information
              </DialogDescription>
            </DialogHeader>
            {selectedMessage && (
              <div className="space-y-4">
                {/* Status and Time */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      selectedMessage.event === 'sent' ? 'secondary' :
                      selectedMessage.event === 'delivered' ? 'default' :
                      selectedMessage.event === 'failed' ? 'destructive' :
                      'outline'
                    }>
                      {selectedMessage.event}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {selectedMessage.timestamp ? new Date(selectedMessage.timestamp).toLocaleString() : 'Unknown'}
                    </span>
                  </div>
                </div>

                {/* Recipient Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Recipient Name</Label>
                    <p className="text-sm font-medium mt-1">
                      {selectedMessage.recipientName || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone Number</Label>
                    <p className="text-sm font-mono font-medium mt-1">
                      {selectedMessage.recipientPhone}
                    </p>
                  </div>
                  {selectedMessage.recipientEmail && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <p className="text-sm mt-1">{selectedMessage.recipientEmail}</p>
                    </div>
                  )}
                  {selectedMessage.recipientType && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Type</Label>
                      <Badge variant="outline" className="mt-1">
                        {selectedMessage.recipientType}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Message Content */}
                <div>
                  <Label className="text-xs text-muted-foreground">Message Content</Label>
                  <div className="mt-2 p-4 bg-muted rounded-lg">
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {selectedMessage.messagePreview || 'No message content available'}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedMessage.messagePreview ? `${selectedMessage.messagePreview.length} characters` : '0 characters'}
                  </p>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setMessagePreviewOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* DNC Tab */}
        <TabsContent value="dnc" className="space-y-4">
          <SectionHeader
            title="Do Not Contact List (SMS)"
            actions={
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Add to DNC
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add to Do Not Contact List</DialogTitle>
                  <DialogDescription>
                    Add a phone number to the SMS DNC list
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="dncPhone">Phone Number</Label>
                    <Input
                      id="dncPhone"
                      value={dncPhone}
                      onChange={(e) => setDncPhone(e.target.value)}
                      placeholder="+1234567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dncReason">Reason</Label>
                    <Select value={dncReason} onValueChange={setDncReason}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="stop_reply">STOP Reply</SelectItem>
                        <SelectItem value="complaint">Complaint</SelectItem>
                        <SelectItem value="invalid">Invalid Number</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleAddToDnc} 
                    className="w-full"
                    disabled={addToDncMutation.isPending}
                  >
                    {addToDncMutation.isPending ? 'Adding...' : 'Add to DNC'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            }
          />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                DNC Compliance Notice
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                All SMS campaigns automatically exclude numbers on this list. STOP replies are processed automatically and added to this list for legal compliance.
              </p>
            </CardContent>
          </Card>

          <DNCTable
            entries={dncList.filter((entry: any) => entry.type === 'sms' || entry.type === 'both').map((e: any) => ({ 
              ...e, 
              id: e.id, 
              addedAt: e.addedAt ? (typeof e.addedAt === 'string' ? e.addedAt : new Date(e.addedAt).toISOString()) : '' 
            }))}
            searchFilter={dncSearch}
            onSearchFilterChange={setDncSearch}
            onRemove={(entry) => handleDeleteDncEntry(entry as DNCEntry)}
            contactType="sms"
            isLoading={false}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={isEditTemplateDialogOpen} onOpenChange={setIsEditTemplateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit SMS Template</DialogTitle>
            <DialogDescription>
              Update the template "{selectedTemplateForEdit?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editTemplateName">Template Name</Label>
              <Input
                id="editTemplateName"
                value={editTemplateForm.name}
                onChange={(e) => setEditTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Template name"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="editTemplateContent">Template Content</Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setMergeTagsTarget('edit');
                    setShowMergeTags(true);
                  }}
                >
                  <Tag className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2">
                <Textarea
                  id="editTemplateContent"
                value={editTemplateForm.content}
                onChange={(e) => setEditTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="SMS message content"
                maxLength={1600}
                rows={6}
                />
              </div>
              <div className={`text-sm mt-1 ${editTemplateForm.content.length > 160 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {editTemplateForm.content.length}/160 characters
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsEditTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTemplate}
              disabled={updateTemplateMutation.isPending || !editTemplateForm.name.trim() || !editTemplateForm.content.trim()}
            >
              {updateTemplateMutation.isPending ? "Updating..." : "Update Template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Template Dialog */}
      <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Template to Folder</DialogTitle>
            <DialogDescription>
              Select a destination folder for "{selectedTemplateForMove?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="moveToFolder">Destination Folder</Label>
              <Select onValueChange={(value) => setSelectedMoveFolder(value === "none" ? null : folders.find(f => String(f.id) === value) || null)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <span>No Folder (Unorganized)</span>
                    </div>
                  </SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={String(folder.id)}>
                      <div className="flex items-center gap-2">
                        <Folder className="h-4 w-4" />
                        <span>{folder.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsMoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleConfirmMoveTemplate(selectedMoveFolder ? String(selectedMoveFolder.id) : null)}
              disabled={moveTemplateMutation.isPending}
            >
              {moveTemplateMutation.isPending ? "Moving..." : "Move to Folder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick SMS Dialog */}
      <Dialog open={isQuickSmsDialogOpen} onOpenChange={(open) => {
        setIsQuickSmsDialogOpen(open);
        if (!open) {
          // Clear error and results when dialog closes
          setSmsError(null);
          setBulkSendResult(null);
          setShowQuickSmsClientSearch(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {getSelectedContactsData().length > 0 
                ? `Send Bulk SMS to ${getSelectedContactsData().length} Contact(s)` 
                : 'Send Quick SMS'}
            </DialogTitle>
            <DialogDescription>
              {getSelectedContactsData().length > 0 
                ? `Send an SMS message to ${getSelectedContactsData().length} selected contacts`
                : 'Send an SMS message directly to a phone number'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Error Display (One-to-One Only) */}
          {smsError && (
            <Alert 
              variant={smsError.includes("Do Not Contact") ? "default" : "destructive"} 
              className={`mt-4 ${smsError.includes("Do Not Contact") ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20" : ""}`}
            >
              {smsError.includes("Do Not Contact") ? (
                <ShieldX className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertTitle>
                {smsError.includes("Do Not Contact") ? "DNC Compliance Block" : "Failed to Send SMS"}
              </AlertTitle>
              <AlertDescription className="mt-2">
                {smsError}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-6">
            {/* Phone Number Selection - Only for one-to-one SMS */}
            {getSelectedContactsData().length === 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label htmlFor="quickSmsPhone">Phone Number</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQuickSmsClientSearch(!showQuickSmsClientSearch)}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    {showQuickSmsClientSearch ? 'Manual Entry' : 'Select Client'}
                  </Button>
                </div>
                
                {showQuickSmsClientSearch ? (
                  <div className="space-y-2">
                    <Select onValueChange={(value) => {
                      const [type, id] = value.split('-');
                      const client = type === 'client' 
                        ? (clients as any[])?.find(c => c.id === parseInt(id))
                        : (prospects as any[])?.find(p => p.id === parseInt(id));
                      setSelectedQuickSmsClient(client);
                      setSelectedQuickSmsClientType(type as 'client' | 'prospect');
                      setOneToOnePhone(client?.phone || client?.contactPhone || '');
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client or prospect..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.isArray(clients) && (clients as any[]).map((client) => (
                          <SelectItem key={`client-${client.id}`} value={`client-${client.id}`}>
                            {client.name} (Client) - {client.phone || client.contactPhone || 'No phone'}
                          </SelectItem>
                        ))}
                        {Array.isArray(prospects) && (prospects as any[]).map((prospect) => (
                          <SelectItem key={`prospect-${prospect.id}`} value={`prospect-${prospect.id}`}>
                            {prospect.name} (Prospect) - {prospect.phone || prospect.contactPhone || 'No phone'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedQuickSmsClient && (
                      <div className="text-sm text-muted-foreground p-2 bg-muted rounded">
                        Selected: {selectedQuickSmsClient.name} - {selectedQuickSmsClient.phone || selectedQuickSmsClient.contactPhone}
                      </div>
                    )}
                  </div>
                ) : (
                  <Input
                    id="quickSmsPhone"
                    value={oneToOnePhone}
                    onChange={(e) => setOneToOnePhone(e.target.value)}
                    placeholder="+1234567890"
                  />
                )}
              </div>
            )}
            
            {/* Recipients Summary - Only for bulk SMS */}
            {getSelectedContactsData().length > 0 && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
                  <Users className="h-5 w-5" />
                  <div>
                    <div className="font-medium">
                      Sending to {getSelectedContactsData().length} selected contact(s)
                    </div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      {getSelectedContactsData().slice(0, 3).map(c => c.name).join(', ')}
                      {getSelectedContactsData().length > 3 && ` and ${getSelectedContactsData().length - 3} more`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* From Number Selection - Only for bulk SMS when there are SMS-enabled numbers */}
            {getSelectedContactsData().length > 0 && smsPhoneNumbers.length > 0 && (
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <PhoneOutgoing className="h-4 w-4" />
                  From Number
                </Label>
                <Select
                  value={selectedFromNumberId || (smsPhoneNumbers.find(p => p.isDefault)?.id?.toString() || smsPhoneNumbers[0]?.id?.toString() || '')}
                  onValueChange={setSelectedFromNumberId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a phone number" />
                  </SelectTrigger>
                  <SelectContent>
                    {smsPhoneNumbers.map((phone) => (
                      <SelectItem key={phone.id} value={String(phone.id)}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{phone.phoneNumber}</span>
                          {phone.friendlyName && (
                            <span className="text-muted-foreground">({phone.friendlyName})</span>
                          )}
                          {phone.isDefault && (
                            <Badge variant="secondary" className="text-xs">Default</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Select the phone number to send SMS from. Enable numbers for SMS in Settings &rarr; Phone & SMS.
                </p>
              </div>
            )}

            {/* No SMS-enabled numbers warning */}
            {getSelectedContactsData().length > 0 && smsPhoneNumbers.length === 0 && (
              <Alert variant="default" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                <Phone className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertTitle>No SMS-Enabled Numbers</AlertTitle>
                <AlertDescription>
                  You don't have any phone numbers enabled for SMS. Go to Settings &rarr; Phone & SMS to purchase and enable numbers.
                </AlertDescription>
              </Alert>
            )}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label htmlFor="quickSmsMessage">Message</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQuickSmsTemplateSelect(!showQuickSmsTemplateSelect)}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {showQuickSmsTemplateSelect ? 'Hide Templates' : 'Load Template'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setMergeTagsTarget('quick_sms');
                      setShowMergeTags(true);
                    }}
                  >
                    <Tag className="h-4 w-4 mr-2" />
                    Merge Tags
                  </Button>
                </div>
              </div>
              <Textarea
                id="quickSmsMessage"
                value={oneToOneMessage}
                onChange={(e) => setOneToOneMessage(e.target.value)}
                placeholder="Enter your SMS message..."
                maxLength={1600}
                rows={4}
              />
              <div className={`text-sm mt-1 ${oneToOneMessage.length > 160 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {oneToOneMessage.length}/160 characters
              </div>
              
              {showQuickSmsTemplateSelect && (
                <div className="mt-3 p-3 border rounded-lg bg-muted">
                  <Label className="text-sm font-medium">Select SMS Template</Label>
                  <Select onValueChange={(templateId) => {
                    const template = templates.find(t => String(t.id) === templateId);
                    if (template) {
                      setOneToOneMessage(template.content);
                      setShowQuickSmsTemplateSelect(false);
                      toast({ title: "Template Loaded", description: `"${template.name}" has been loaded` });
                    }
                  }}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={String(template.id)}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsQuickSmsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendOneToOne}
              disabled={
                (sendOneToOneMutation.isPending || sendBulkSmsMutation.isPending) ||
                (getSelectedContactsData().length > 0 ? !oneToOneMessage.trim() : (!oneToOnePhone.trim() || !oneToOneMessage.trim()))
              }
            >
              {(sendOneToOneMutation.isPending || sendBulkSmsMutation.isPending) 
                ? "Sending..." 
                : (getSelectedContactsData().length > 0 
                  ? `Send to ${getSelectedContactsData().length} Contact(s)` 
                  : "Send SMS")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deletion Confirmation Dialogs */}
      {/* Template Deletion Confirmation */}
      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SMS Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{templateToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTemplateToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (templateToDelete) {
                  deleteTemplateMutation.mutate(String(templateToDelete.id));
                }
              }}
              disabled={deleteTemplateMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTemplateMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Folder Deletion Confirmation */}
      <AlertDialog open={!!folderToDelete} onOpenChange={(open) => !open && setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{folderToDelete?.name}</strong>? 
              {folderToDelete && getTemplatesInFolder(Number(folderToDelete.id)).length > 0 && (
                <> This folder contains {getTemplatesInFolder(Number(folderToDelete.id)).length} template(s). 
                They will be moved to unorganized templates.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFolderToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (folderToDelete) {
                  deleteFolderMutation.mutate(String(folderToDelete.id));
                }
              }}
              disabled={deleteFolderMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteFolderMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* List Deletion Confirmation */}
      <AlertDialog open={listDeleteDialogOpen} onOpenChange={setListDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Smart List?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{listToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setListToDelete(null);
              setListDeleteDialogOpen(false);
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (listToDelete) {
                  deleteListMutation.mutate(listToDelete.id);
                }
              }}
              disabled={deleteListMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteListMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit List Dialog */}
      <EditListDialog
        isOpen={isEditListDialogOpen}
        onOpenChange={(open) => {
          setIsEditListDialogOpen(open);
          if (!open) {
            setListToEdit(null);
            setEditListName('');
            setEditListDescription('');
            setEditListSelectedContacts(new Set());
          }
        }}
        list={listToEdit}
        listName={editListName}
        onListNameChange={setEditListName}
        listDescription={editListDescription}
        onListDescriptionChange={setEditListDescription}
        selectedContacts={editListSelectedContacts}
        onSelectedContactsChange={setEditListSelectedContacts}
        availableContacts={contacts.map(c => ({ ...c, phone: c.phone || '', email: c.email || '' }))}
        onSave={() => {
          if (!editListName.trim()) {
            toast({
              title: 'Error',
              description: 'Please enter a list name.',
              variant: 'destructive',
            });
            return;
          }
          if (listToEdit) {
            const contactIdsArray = Array.from(editListSelectedContacts);
            updateListMutation.mutate({
              id: listToEdit.id,
              name: editListName,
              description: editListDescription,
              contactIds: contactIdsArray,
              contactCount: contactIdsArray.length,
            });
          }
        }}
        onCancel={() => {
          setIsEditListDialogOpen(false);
          setListToEdit(null);
          setEditListName('');
          setEditListDescription('');
          setEditListSelectedContacts(new Set());
        }}
        isLoading={updateListMutation.isPending}
        contactType="sms"
      />

      {/* DNC Entry Deletion Confirmation */}
      <AlertDialog open={!!dncEntryToDelete} onOpenChange={(open) => !open && setDncEntryToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from DNC List?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{dncEntryToDelete?.phone || dncEntryToDelete?.email}</strong> from the Do Not Contact list?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDncEntryToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (dncEntryToDelete) {
                  removeFromDncMutation.mutate(dncEntryToDelete.id);
                }
              }}
              disabled={removeFromDncMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeFromDncMutation.isPending ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}